/**
 * Customer Document Share Links
 *
 * Creates time-limited public tokens that point to /doc/{token}.
 * Guest access requires a persisted document_shares row resolved via RPC.
 */

import { dataProvider as supabase } from "@/lib/providers/data-provider";
import { documentShareUrl } from "@/lib/link-hosts";
import { useSettings } from "@/lib/settings-store";
import { useBilling } from "@/lib/billing-store";
import { useOrders } from "@/lib/orders-store";
import { useRepairs } from "@/lib/repair-store";
import { useMfgBills } from "@/lib/manufacturing-bill-store";
import type { CommRequest } from "@/lib/comm/types";
import { getDocumentHostingDefaultDays } from "@/lib/platform/platform-runtime-config";
import { loadFirmCommunicationSettings } from "@/lib/firm-communication-settings";
import { maxDocumentShareDaysForCurrentScale } from "@/lib/platform/supabase-free-tier";

export type ShareDocumentType =
  | "invoice"
  | "order"
  | "repair"
  | "job"
  | "estimate"
  | "delivery_challan"
  | "credit_note"
  | "debit_note"
  | "gold_settlement";

/** Platform default: 7 days (overridden by platform_settings via getDocumentHostingDefaultDays). */
export const DEFAULT_DOCUMENT_HOSTING_DAYS = 7;
export const MIN_DOCUMENT_HOSTING_DAYS = 1;
export const MAX_DOCUMENT_HOSTING_DAYS = 365;

let cachedHostingAllowed: { value: boolean; expiresAt: number } | null = null;

export async function canUseDocumentHosting(): Promise<boolean> {
  const now = Date.now();
  if (cachedHostingAllowed && cachedHostingAllowed.expiresAt > now) {
    return cachedHostingAllowed.value;
  }
  try {
    const settings = await loadFirmCommunicationSettings();
    cachedHostingAllowed = { value: settings.publicShareEnabled, expiresAt: now + 60_000 };
    return settings.publicShareEnabled;
  } catch {
    return true;
  }
}

export interface DocumentShare {
  id: string;
  document_type: ShareDocumentType;
  document_id: string;
  party_id?: string | null;
  firm_snapshot: Record<string, unknown>;
  document_snapshot: Record<string, unknown>;
  form_metadata?: Record<string, unknown> | null;
  expires_at: string;
  retention_expires_at?: string | null;
  created_at: string;
  branch_id?: string | null;
}

function randomToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function tokenHash(token: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function resolveDocumentData(docType: ShareDocumentType, docId: string): unknown {
  if (docType === "invoice" || docType === "estimate") {
    return useBilling.getState().invoices.find((i) => i.id === docId || i.invoiceNo === docId) ?? null;
  }
  if (docType === "order") {
    return useOrders.getState().orders.find((o) => o.id === docId || o.orderNo === docId) ?? null;
  }
  if (docType === "repair") {
    return useRepairs.getState().repairs.find((r) => r.id === docId || r.repairNo === docId) ?? null;
  }
  if (docType === "job") {
    const s = useMfgBills.getState() as { bills?: unknown[]; mfgBills?: unknown[]; items?: unknown[] };
    const list: unknown[] = s.bills ?? s.mfgBills ?? s.items ?? [];
    return list.find(
      (b) =>
        typeof b === "object" &&
        b !== null &&
        ((b as { id?: string }).id === docId || (b as { billNo?: string }).billNo === docId),
    ) ?? null;
  }
  return null;
}

/**
 * Creates a time-limited share link. Fails (returns null) if DB insert does not succeed.
 */
export async function createDocumentShareLink(
  docType: ShareDocumentType,
  docId: string,
  branchId: string,
  expiresInDays?: number,
): Promise<string | null> {
  try {
    const hostingAllowed = await canUseDocumentHosting();
    if (!hostingAllowed) {
      console.warn("[DocumentShares] Public document hosting is disabled for this firm");
      return null;
    }

    const firm = useSettings.getState().firm;
    const docSnapshot = resolveDocumentData(docType, docId);
    if (!docSnapshot) {
      console.error("[DocumentShares] Document not found:", docType, docId);
      return null;
    }

    const platformDefaultDays = await getDocumentHostingDefaultDays();
    const requestedDays = expiresInDays ?? platformDefaultDays ?? DEFAULT_DOCUMENT_HOSTING_DAYS;
    const cappedDays = maxDocumentShareDaysForCurrentScale(requestedDays);
    const days = Math.min(
      Math.max(cappedDays, MIN_DOCUMENT_HOSTING_DAYS),
      MAX_DOCUMENT_HOSTING_DAYS,
    );
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + days);
    const token = randomToken();
    const partyId =
      typeof docSnapshot === "object" && docSnapshot !== null
        ? ((docSnapshot as { customerId?: string; partyId?: string; personId?: string }).customerId ??
          (docSnapshot as { partyId?: string }).partyId ??
          (docSnapshot as { personId?: string }).personId ??
          null)
        : null;

    const formMetadata = {
      sharedAt: new Date().toISOString(),
      docType,
      branchId: branchId || null,
      ttlDays: days,
    };

    const hash = await tokenHash(token);
    const { data: inserted, error: dbErr } = await (supabase as any)
      .from("document_shares")
      .insert({
        document_type: docType,
        document_id: docId,
        party_id: partyId,
        firm_snapshot: firm,
        document_snapshot: docSnapshot,
        form_metadata: formMetadata,
        token_hash: hash,
        expires_at: expiresAt.toISOString(),
        retention_expires_at: expiresAt.toISOString(),
        branch_id: branchId || null,
        created_by: (await supabase.auth.getSession()).data.session?.user?.id ?? null,
      })
      .select("id")
      .maybeSingle();

    if (dbErr || !inserted?.id) {
      console.error("[DocumentShares] Database insert failed:", dbErr?.message ?? "no row returned");
      return null;
    }

    const shareUrl = documentShareUrl(token);
    void import("@/lib/automation/automation-store").then(({ useAutomationEngine }) => {
      void useAutomationEngine.getState().emitEvent(
        "DOCUMENT_SHARED",
        {
          documentType: docType,
          documentId: docId,
          branchId,
          shareUrl,
          expiresAt: expiresAt.toISOString(),
          ttlDays: days,
        },
        { branchId },
      );
    });

    return shareUrl;
  } catch (err) {
    console.error("[DocumentShares] Share creation error:", err);
    return null;
  }
}

/**
 * Fetches a share record by token via server RPC only (guest-safe).
 */
export async function getDocumentShare(token: string): Promise<DocumentShare | null> {
  if (!token || token.length < 32) return null;

  try {
    const { data, error } = await (supabase as any).rpc("resolve_document_share", {
      p_token: token,
    });
    if (!error && data) {
      const share = data as DocumentShare;
      if (share.expires_at && new Date(share.expires_at).getTime() <= Date.now()) {
        return null;
      }
      return share;
    }
    if (error) {
      console.warn("[DocumentShares] resolve_document_share failed:", error.message);
    }
  } catch (err) {
    console.warn("[DocumentShares] resolve_document_share error:", err);
  }

  return null;
}

/** Maps CommRequest.linkedType → ShareDocumentType */
export function linkedTypeToShareDocType(linkedType: CommRequest["linkedType"]): ShareDocumentType {
  if (!linkedType) return "invoice";
  return linkedType as ShareDocumentType;
}
